// ViewHeader — the one header a view opens with (D41, 2026-10-06): Bibliotek,
// Journal, Inställningar and any future view. Ny chatt has none (it explains
// itself). Pure, props-driven.
//
// Anatomy, top to bottom, every row optional except the title:
//   1. title row — the VIEW's name as the page's h1, + `actions` (e.g. a ⋮
//      menu) beside it, never pushed to the far edge (proximity: the menu
//      belongs to the title it acts on).
//   2. `tools`   — a control that shapes the content (Bibliotek's search).
//   3. `nav`     — sub-navigation (tabs). Always the LAST row, so it touches
//      the content it switches (proximity; the ARIA tab → tabpanel reading
//      order), and search + tabs read as one block in Bibliotek.
//
// Layout contract: a view lays out in ONE `ViewColumn` (below) and opens with
// the ViewHeader as its first child. The header never sets a width of its own,
// so title, tabs and content share the column's left edge and centring axis in
// every view. The header owns the inset above the title; the column owns the
// gutter, the envelope and the gap between header and content. No view adds
// its own padding or width on top (D41: identical placement, measured).

import type { ReactNode } from "react";
import { Heading } from "../Heading/Heading.js";
import "./ViewHeader.css";

export type ViewHeaderProps = {
    // The view's name (localised, caller-owned): "Bibliotek", "Journal", …
    readonly title: ReactNode;
    readonly actions?: ReactNode;
    readonly tools?: ReactNode;
    readonly nav?: ReactNode;
};

export const ViewHeader = ({ title, actions, tools, nav }: ViewHeaderProps) => (
    <header className="bilda-view-header">
        <div className="bilda-view-header__title">
            <Heading level={1} size="2xl">
                {title}
            </Heading>
            {actions}
        </div>
        {tools}
        {nav}
    </header>
);

export type ViewColumnProps = {
    // The view's own additions (e.g. the Library's fill), never a width or
    // an inset: those are the column's.
    readonly className?: string;
    readonly children: ReactNode;
};

// The one column a view lays out in: header first, then the content.
export const ViewColumn = ({ className, children }: ViewColumnProps) => (
    <div className={`bilda-view-column${className ? ` ${className}` : ""}`}>
        {children}
    </div>
);
